// WeekRibbon - 다가오는 N주의 일정 밀도 (§4-1 시간축).
//
// KPI 카드는 "지금 몇 건"에 답한다. 리본은 "언제 몰려 있는가"에만
// 답한다 - 두 질문은 다르고, 카드로는 두 번째에 답할 수 없다.
//
// 색은 상태에서만 온다. 리본의 뼈대(칸·규칙선·라벨)는 전부 무채색이고,
// 칠해지는 것은 그 주의 상태뿐이다.
//
// **형태 결정 (2026-09, 리디자인)**
// 이전 판은 44px 높이에 16px 막대 + 10px 라벨을 쌓은 스파크라인이었다.
// 세 가지가 깨져 있었다.
//   1. 건수가 10px 라 주차 라벨과 같은 급이었다 - 대시보드에서 제일 먼저
//      읽혀야 할 숫자가 제일 작았다.
//   2. 막대 높이에 상한(5건)이 있어 5건과 12건이 같은 높이였다. 몰림을
//      보라고 만든 그래프가 몰림을 못 보여줬다.
//   3. 한 칸이 최고 심각도 색 하나로만 칠해져, 4건 안에 지연이 섞였는지
//      알 수 없었다.
// 지금은 (1) 건수를 타일의 주인공으로 올리고, (2) 높이 대신 면적·틴트로
// 밀도를 말하고, (3) 타일 아래 4px 스택 바가 상태 구성을 그대로 편다.
// 일정이 0 인 주는 칠하지 않는다 - 바쁜 주만 남아 눈이 갈 곳이 하나다.
//
// 오른쪽 「다가오는 일정」은 타일이 답하지 않는 다음 질문("그래서 뭐가
// 먼저 오나")을 맡는다. 칸을 열어보게 하지 않는다.
import { STATUS, StatusDot } from "@/components/StatusIndicator";
import { type RibbonCell, type RibbonEvent, cellLabels, upcomingEvents } from "@/lib/ribbon";
import { STATUS_CODES, statusKey } from "@/lib/status";

/** 옆에 세우는 「다가오는 일정」 줄 수. 4줄이면 타일 높이와 균형이 맞는다. */
const UPCOMING = 4;

/** 타일 한 칸이 이름으로 보여줄 일정 수. 넘치면 `+N` 으로 접는다. */
const TILE_LABELS = 3;

/** 상태 색은 StatusIndicator 의 메타가 정본이다 - 여기서 다시 만들지 않는다. */
const statusColor = (code: NonNullable<RibbonCell["peak"]>) => STATUS[statusKey(code)].color;
const statusSoft = (code: NonNullable<RibbonCell["peak"]>) => STATUS[statusKey(code)].soft;

/** "2026-09-08" → "9/8 월". 리스트에서 날짜가 제일 먼저 읽혀야 한다. */
const DOW = ["일", "월", "화", "수", "목", "금", "토"];
const shortDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${m}/${d} ${DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]}`;
};

export const WeekRibbon = ({
  cells,
  loading,
  onSelectWeek,
}: {
  cells: RibbonCell[];
  loading?: boolean;
  /** 칸 클릭 - §13 일정 화면을 그 주로 열어 준다. */
  onSelectWeek?: (cell: RibbonCell) => void;
}) => {
  const total = cells.reduce((n, c) => n + c.events.length, 0);
  const upcoming = upcomingEvents(cells, UPCOMING);

  return (
    <section
      aria-label="다가오는 일정"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4) var(--space-5) var(--space-5)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          marginBottom: "var(--space-4)",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "var(--fs-micro)",
            lineHeight: "var(--lh-micro)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontWeight: "var(--fw-semibold)",
            color: "var(--text-tertiary)",
          }}
        >
          향후 {cells.length}주
        </h2>
        <span style={{ display: "flex", alignItems: "center", gap: "var(--space-4)" }}>
          <RibbonLegend cells={cells} />
          <span
            style={{
              fontFamily: "var(--font-num)",
              fontSize: "var(--fs-caption)",
              color: "var(--text-tertiary)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {loading ? "…" : `${total}건`}
          </span>
        </span>
      </header>

      {/* 타일과 목록을 한 줄에 둔다. 좁아지면 목록이 아래로 접힌다 -
          타일이 8칸을 유지해야 "8주"라는 말이 사실로 남는다. */}
      <div className="grid grid-cols-1 items-start gap-s5 lg:grid-cols-[minmax(0,1fr)_18rem]">
        {/* 빈 상태에도 뼈대는 남긴다 - 칸이 사라지면 "일정이 없다"가 아니라
            "리본이 고장났다"로 읽힌다 (REQ-ALERT-008 의 0 붕괴와 다른 경우다:
            여기서 0 은 정보다). */}
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))`,
            // 기본 stretch - 8칸이 가장 큰 칸(일정 이름이 제일 많은 칸)에
            // 맞춰 같은 높이로 선다. 바깥 2열이 items-start 라 이 높이는
            // 옆의 「다가오는 일정」이 아니라 타일 내용이 정한다.
            gap: "var(--space-2)",
          }}
        >
          {cells.map((cell) => (
            <WeekTile key={cell.bucket.key} cell={cell} onSelectWeek={onSelectWeek} />
          ))}
        </ol>

        <UpcomingList events={upcoming} loading={loading} />
      </div>
    </section>
  );
};

const WeekTile = ({
  cell,
  onSelectWeek,
}: {
  cell: RibbonCell;
  onSelectWeek?: (cell: RibbonCell) => void;
}) => {
  const n = cell.events.length;
  const labels = cell.events.map((e) => e.eventLabel);
  const title =
    n === 0
      ? `${cell.bucket.label} · 일정 없음`
      : `${cell.bucket.label} · ${labels.slice(0, 4).join(", ")}${n > 4 ? ` 외 ${n - 4}건` : ""}`;
  const Tag = onSelectWeek ? "button" : "div";

  return (
    <li style={{ minWidth: 0 }}>
      <Tag
        {...(onSelectWeek ? { type: "button" as const, onClick: () => onSelectWeek(cell) } : {})}
        title={title}
        aria-label={title}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          padding: "10px 4px 8px",
          border: 0,
          borderRadius: "var(--radius-control)",
          // 일정이 있는 주만 칠한다. 0 인 주는 배경 없이 뒤로 물러난다.
          background: cell.peak ? statusSoft(cell.peak) : "var(--surface-inset)",
          // 이번 주는 색이 아니라 링으로 표시한다 - 색은 상태 전용이다.
          boxShadow: cell.current ? "inset 0 0 0 2px var(--text-primary)" : "none",
          font: "inherit",
          cursor: onSelectWeek ? "pointer" : "default",
          textAlign: "center",
          transition: "background var(--dur-base) var(--ease-out)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-num)",
            fontSize: "var(--fs-title)",
            lineHeight: 1.05,
            fontVariantNumeric: "tabular-nums",
            fontWeight: "var(--fw-semibold)",
            letterSpacing: "-0.02em",
            color: cell.peak ? statusColor(cell.peak) : "var(--text-quaternary)",
          }}
        >
          {n}
        </span>
        <span
          style={{
            fontSize: "var(--fs-caption)",
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            color: cell.current ? "var(--text-primary)" : "var(--text-tertiary)",
            fontWeight: cell.current ? "var(--fw-semibold)" : "var(--fw-regular)",
          }}
        >
          {cell.bucket.month}/{cell.bucket.week}주
        </span>
        {/* 그 주에 무엇이 걸렸는지. 숫자만 있으면 "4건이 뭔데?"를 물어보러
            칸을 눌러야 한다. 넘치는 건수는 `+N`, 전체 목록은 title 속성. */}
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            width: "100%",
            minWidth: 0,
            fontSize: 10.5,
            lineHeight: 1.3,
            color: n > 0 ? "var(--text-secondary)" : "var(--text-quaternary)",
          }}
        >
          {n === 0 ? (
            <span>일정 없음</span>
          ) : (
            cellLabels(cell, TILE_LABELS).map((label) => (
              <span
                key={label}
                style={{
                  maxWidth: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </span>
            ))
          )}
        </span>
        {/* 상태 스택 - 그 주의 구성비. 숫자 하나로는 "4건 중 몇 건이
            지연인지"를 말할 수 없다. 폭이 건수 비율, 색이 상태다. */}
        <span
          aria-hidden="true"
          style={{
            display: "flex",
            gap: 1,
            width: "100%",
            height: 4,
            minWidth: 0,
            // 이름 줄 수가 칸마다 달라도 스택 바는 항상 바닥에 붙는다.
            marginTop: "auto",
          }}
        >
          {n > 0 &&
            STATUS_CODES.filter((code) => cell.counts[code] > 0).map((code) => (
              <span
                key={code}
                style={{
                  flex: cell.counts[code],
                  borderRadius: 999,
                  background: statusColor(code),
                }}
              />
            ))}
        </span>
      </Tag>
    </li>
  );
};

/**
 * 다가오는 일정 - 리본 전체에서 가장 먼저 오는 몇 건.
 *
 * 비어 있으면 줄만 남기고 조용히 있는다. "8주간 일정 없음"은 사실이고,
 * 그 사실을 말하는 것과 빈 박스를 두는 것은 다르다.
 */
const UpcomingList = ({ events, loading }: { events: RibbonEvent[]; loading?: boolean }) => (
  <div className="flex flex-col gap-s2">
    <span
      style={{
        fontSize: "var(--fs-micro)",
        lineHeight: "var(--lh-micro)",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: "var(--fw-semibold)",
        color: "var(--text-tertiary)",
      }}
    >
      다가오는 일정
    </span>
    {events.length === 0 ? (
      <span style={{ fontSize: "var(--fs-caption)", color: "var(--text-tertiary)" }}>
        {loading ? "불러오는 중" : "예정된 일정이 없어요."}
      </span>
    ) : (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {events.map((e, i) => (
          <li
            key={`${e.date}-${e.devItemId ?? ""}-${e.eventLabel}`}
            style={{
              display: "grid",
              gridTemplateColumns: "3.6rem minmax(0, 1fr) auto",
              alignItems: "center",
              gap: "var(--space-3)",
              padding: "var(--space-2) 0",
              borderTop: i === 0 ? 0 : "1px solid var(--border-subtle)",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-num)",
                fontSize: "var(--fs-caption)",
                fontVariantNumeric: "tabular-nums",
                color: "var(--text-secondary)",
              }}
            >
              {shortDate(e.date)}
            </span>
            <span
              style={{
                fontSize: "var(--fs-label)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {e.partName ? (
                <>
                  {e.partName}{" "}
                  <span style={{ color: "var(--text-tertiary)" }}>{e.eventLabel}</span>
                </>
              ) : (
                e.eventLabel
              )}
            </span>
            {/* 옆에 같은 뜻의 텍스트가 없다 - labelled 를 켜면 상태가
                스크린리더에서 사라진다. */}
            <StatusDot status={statusKey(e.status)} size="row" />
          </li>
        ))}
      </ul>
    )}
  </div>
);

const RibbonLegend = ({ cells }: { cells: RibbonCell[] }) => {
  const seen = [...new Set(cells.map((c) => c.peak).filter((s): s is NonNullable<typeof s> => !!s))];
  if (seen.length === 0) return null;
  return (
    <span
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--space-1) var(--space-3)",
        fontSize: "var(--fs-caption)",
        color: "var(--text-tertiary)",
      }}
    >
      {seen.map((code) => (
        <span key={code} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <StatusDot status={statusKey(code)} size="md" labelled />
          {STATUS[statusKey(code)].label}
        </span>
      ))}
    </span>
  );
};
